/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  confirmTwoFactorSetup,
  startTwoFactorSetup,
  twoFactorErrorMessage,
  TwoFactorSessionResponse,
  TwoFactorSetupResponse,
} from '@nxsys/services/auth/twoFactor';
import { message } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Si1Password, SiAuthy, SiGoogle, SiMicrosoft } from 'react-icons/si';
import CodeInput from './CodeInput';
import ScanPanel from './ScanPanel';
import {
  StyledBlockedNotice,
  StyledCodesPanel,
  StyledConfirmRow,
  StyledDialog,
  StyledDone,
  StyledFooter,
  StyledGhostButton,
  StyledHint,
  StyledLogoLockup,
  StyledMethodCard,
  StyledOverlay,
  StyledPanel,
  StyledPrimaryButton,
  StyledRail,
  StyledStepBody,
  StyledStepper,
  StyledTextButton,
} from './index.styled';

interface Props {
  baseUrl: string;
  challengeToken: string;
  email?: string;
  onComplete: (session: TwoFactorSessionResponse) => void;
  /**
   * Fired the instant enrolment is confirmed, with the app code still inside its
   * TOTP window (Victor, review of #1755).
   *
   * Separate from `onComplete` because of timing, not tidiness. The dual-system
   * login needs this same code posted to the *other* backend, but `onComplete`
   * fires at the end of the wizard — after the user has read, copied or
   * downloaded ten recovery codes and clicked twice — and the backend accepts
   * only `TOTP_WINDOW = 1`, roughly 60 seconds. Handing the code over at
   * `onComplete` would therefore have failed for most users and shown a
   * "needs a fresh code" message instead of registering the second system, which
   * looks fixed without being fixed. The caller starts the peer call here and
   * awaits it at `onComplete`.
   */
  onEnrolled?: (appCode: string) => void;
  /**
   * Only set on the settings path, where enrolment is voluntary and Cancel has
   * somewhere to go. Left undefined at login, which passes `onLeave` instead —
   * and its presence is what tells the two paths apart.
   */
  onCancel?: () => void;
  /**
   * The challenge token has lapsed and nothing here can succeed any more
   * (Victor, review of #1755). Mandatory enrolment had no way out of that: this
   * step asks the user to go and install an authenticator app, the token lives
   * five minutes, Escape is swallowed and the overlay covers the page — so only
   * a browser reload escaped. The caller drops the pending challenge and shows
   * the sign-in form again.
   *
   * The backend's pending secret lives fifteen minutes, so signing in again and
   * scanning the same QR still works.
   */
  onExpired?: () => void;
  /**
   * The user chose to stop before enrolment finished (Top, 19/08). Same
   * destination as `onExpired` — drop the challenge, show the sign-in form — but
   * nothing has gone wrong, so the caller says nothing about it.
   *
   * The button reads "Back to sign in" rather than "Log out" because at this
   * point there is no session to end, only a challenge token that was never
   * redeemed, and the sign-in form is what the user lands on.
   */
  onLeave?: () => void;
}

const STEPS = [
  { label: 'Method', note: 'How you receive codes' },
  { label: 'Connect', note: 'Scan or paste the key' },
  { label: 'Verify', note: 'Confirm it works' },
  { label: 'Backup', note: 'Save recovery codes' },
];

const COPY_FEEDBACK_MS = 1600;
const EMPTY_DIGITS = ['', '', '', '', '', ''];

/**
 * Mandatory two-factor enrolment (NX-4996), built from
 * `design_handoff_2fa_setup/README.md`.
 *
 * Rendered over the dimmed app: Escape does nothing and the overlay cannot be
 * clicked away, because the user cannot reach the app until enrolment finishes.
 * The one deliberate exit is the first step's left button — "Cancel" from
 * settings, "Back to sign in" at login. `onCancel` is what distinguishes the two
 * paths: present from settings, where enrolment is voluntary and there is
 * somewhere to go back to; absent at login, which passes `onLeave`.
 */
const TwoFactorSetup = ({
  baseUrl,
  challengeToken,
  email,
  onComplete,
  onEnrolled,
  onCancel,
  onExpired,
  onLeave,
}: Props) => {
  const [step, setStep] = useState(0);
  const [setup, setSetup] = useState<TwoFactorSetupResponse | null>(null);
  const [loadingSetup, setLoadingSetup] = useState(false);
  const [digits, setDigits] = useState<string[]>(EMPTY_DIGITS);
  /**
   * Bumped on a rejected code, and used as the code row's `key`. Remounting is
   * what re-runs its `autoFocus`, so the caret lands back in the first box
   * without CodeInput growing a "focus now" prop for one caller.
   */
  const [attempt, setAttempt] = useState(0);
  const [status, setStatus] = useState<'idle' | 'verifying' | 'error'>('idle');
  const [errorText, setErrorText] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [session, setSession] = useState<TwoFactorSessionResponse | null>(null);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState<'key' | 'codes' | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!copied) return undefined;
    const id = setTimeout(() => setCopied(null), COPY_FEEDBACK_MS);
    return () => clearTimeout(id);
  }, [copied]);

  // Escape must not dismiss — the flow is mandatory.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') event.preventDefault();
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, []);

  /**
   * A lapsed challenge token and a wrong code are both 401, so the message is
   * what tells them apart — the backend says the password is needed again. Same
   * test the settings page uses. If the wording ever drifts the user is left
   * reading the message instead of being sent back, which is the old behaviour
   * rather than a new failure.
   */
  const handleIfExpired = (text: string): boolean => {
    if (!/password again|expired|sign in again/i.test(text)) return false;
    onExpired?.();
    return true;
  };

  /** Resolves to whether a secret is now in hand, so the caller can stay put. */
  const loadSetup = useCallback(async (): Promise<boolean> => {
    if (setup) return true;
    if (loadingSetup) return false;
    setLoadingSetup(true);
    try {
      setSetup(await startTwoFactorSetup(baseUrl, challengeToken));
      return true;
    } catch (error) {
      const text = twoFactorErrorMessage(
        error,
        'We could not start two-factor setup. Please sign in again.',
      );
      message.error(text);
      handleIfExpired(text);
      return false;
    } finally {
      setLoadingSetup(false);
    }
  }, [baseUrl, challengeToken, loadingSetup, setup]);

  const copy = async (text: string, which: 'key' | 'codes') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
    } catch {
      message.error(
        'Copying is blocked in this browser. Select the text instead.',
      );
    }
  };

  const downloadCodes = () => {
    const blob = new Blob([backupCodes.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'nxsys-recovery-codes.txt';
    anchor.click();
    URL.revokeObjectURL(url);
  };

  /**
   * Fired by the sixth digit landing, not by a button (Thang, 17/08). The code
   * is passed in rather than read from `digits`, because the state holding it
   * has not re-rendered yet at the moment the row fills.
   */
  const verify = async (code: string) => {
    if (code.length !== 6) return;
    setStatus('verifying');
    setErrorText('');
    try {
      const result = await confirmTwoFactorSetup(baseUrl, challengeToken, code);
      setSession(result);
      setBackupCodes(result.backupCodes || []);
      // Before the step transition, not after: the code is spendable on the other
      // deployment for about a minute, and everything past this point is the user
      // reading their recovery codes.
      onEnrolled?.(code);
      setStatus('idle');
      setStep(3);
    } catch (error) {
      setStatus('error');
      const text = twoFactorErrorMessage(
        error,
        'That code did not match. Try the code showing right now.',
      );
      setErrorText(text);
      if (handleIfExpired(text)) return;
      // Empty the row and put the caret back in the first box. With a button the
      // user could just press it again; without one, six full boxes and no
      // cursor is a wall — and by now the app is showing a different code
      // anyway, so every digit has to be retyped regardless.
      setDigits(EMPTY_DIGITS);
      setAttempt((n) => n + 1);
    }
  };

  const goNext = async () => {
    if (step === 0) {
      // Advance only once the secret is actually in hand (Vincent, review of
      // #1755). Step 1 renders nothing without it and its primary button is
      // disabled, so moving on regardless parked the user in front of a blank QR
      // — and with Escape suppressed and the overlay covering the page, a
      // reload was the only way out. Staying here makes Continue itself the
      // retry.
      if (await loadSetup()) setStep(1);
      return;
    }
    if (step === 1) {
      setStep(2);
      return;
    }
    // No step 2 here: that step renders no primary button, because the sixth
    // digit is the submit.
    if (step === 3) {
      setStep(4);
    }
  };

  const goBack = () => {
    if (step === 0) {
      // Both paths leave from here, to different places: settings back to the
      // security page, login back to the sign-in form.
      if (onCancel) onCancel();
      else onLeave?.();
      return;
    }
    setStep((current) => current - 1);
  };

  /**
   * Index 2 is never read — that step has no primary button.
   *
   * Step 3 says 'Continue', not 'Turn on two-factor' (Thang, 18/08): by then
   * `confirmSetup` has already succeeded, so 2FA is on and the button only moves
   * the user off the recovery codes. The old label promised an action that had
   * happened one step earlier.
   */
  const primaryLabel = ['Continue', "I've scanned it", '', 'Continue'][step];
  const primaryDisabled = (step === 1 && !setup) || (step === 3 && !saved);
  /**
   * The first step's left button leaves the wizard; the middle steps' goes back
   * one step. Not "Log out" on the mandatory path (Top, 19/08): the user holds a
   * challenge token, not a session, so there is nothing to log out of.
   */
  const leaveLabel = onCancel ? 'Cancel' : 'Back to sign in';
  const backLabel = step === 0 ? leaveLabel : 'Back';

  return (
    <StyledOverlay
      role="dialog"
      aria-modal="true"
      aria-label="Set up two-factor authentication"
    >
      <StyledDialog ref={dialogRef}>
        <StyledRail>
          <StyledLogoLockup>
            <img src="/assets/images/logo-white-with-name.svg" alt="Nxsys" />
          </StyledLogoLockup>

          <div>
            <h1 className="tfa-rail-title">Set up two-factor authentication</h1>
            <p className="tfa-rail-body">
              A second check at every sign-in, so a stolen password on its own
              is not enough.
            </p>
          </div>

          <StyledStepper>
            {STEPS.map((item, index) => {
              const state =
                index < step ? 'done' : index === step ? 'current' : 'upcoming';
              return (
                <li key={item.label} className={state}>
                  <div className="col">
                    <span className="bullet">
                      {state === 'done' ? '✓' : index + 1}
                    </span>
                    {index < STEPS.length - 1 && <span className="line" />}
                  </div>
                  <div>
                    <div className="label">{item.label}</div>
                    <div className="note">{item.note}</div>
                  </div>
                </li>
              );
            })}
          </StyledStepper>

          <div className="tfa-rail-foot">
            <span className="dot" />
            Takes about two minutes
          </div>
        </StyledRail>

        <StyledPanel>
          {step === 0 && (
            <StyledStepBody>
              {/* Mandatory path only: on the settings path enrolment is
                  voluntary, so "access is paused" would not be true.

                  Not dismissible. It is the standing reason enrolment cannot
                  be skipped, so hiding it would only leave the user without the
                  explanation while the situation it describes carries on. */}
              {!onCancel && (
                <StyledBlockedNotice>
                  <span className="bang">!</span>
                  <div>
                    <div className="title">
                      You need to set up two-factor authentication before you
                      can continue
                    </div>
                    <div className="body">
                      Access to Nxsys is paused until this is complete. It only
                      takes a couple of minutes.
                    </div>
                  </div>
                </StyledBlockedNotice>
              )}
              {/* No heading over this step: there is exactly one method, so
                  "Choose your second factor" asked a question with one answer,
                  and the rail on the left already says which step this is
                  (Thang, 17/08). Bring the heading back the day a second card
                  appears. */}
              <StyledMethodCard type="button" $selected>
                <span className="marks" aria-hidden="true">
                  <SiGoogle />
                  <SiMicrosoft />
                  <Si1Password />
                  <SiAuthy />
                </span>
                <span className="title">Authenticator app</span>
                <span className="desc">
                  Use Google Authenticator or Microsoft Authenticator — or any
                  other TOTP app. Codes are generated on your device and work
                  offline.
                </span>
              </StyledMethodCard>
            </StyledStepBody>
          )}

          {step === 1 && (
            <StyledStepBody>
              <h2>Scan this with your preferred 2FA app</h2>
              <p className="sub">
                Google Authenticator, 1Password, Authy — any TOTP app works.
                Nxsys will appear as a new entry.
              </p>
              <ScanPanel
                qrDataUrl={setup?.qrDataUrl}
                secret={setup?.secret}
                email={email}
              />
            </StyledStepBody>
          )}

          {step === 2 && (
            <StyledStepBody>
              <h2>Enter the 6-digit code</h2>
              <p className="sub">
                Type the code your app shows for Nxsys. It refreshes every 30
                seconds.
              </p>
              <CodeInput
                key={attempt}
                autoFocus
                digits={digits}
                status={status}
                onChange={(next) => {
                  setDigits(next);
                  if (status === 'error') {
                    setStatus('idle');
                    setErrorText('');
                  }
                }}
                onComplete={(code) => verify(code)}
              />
              <StyledHint $error={status === 'error'}>
                {status === 'verifying'
                  ? 'Checking…'
                  : status === 'error'
                    ? errorText
                    : 'Six digits, no spaces. Paste works too.'}
              </StyledHint>
            </StyledStepBody>
          )}

          {step === 3 && (
            <StyledStepBody>
              <h2>Save your recovery codes</h2>
              <p className="sub">
                Each code signs you in once if you lose your phone. A password
                manager is the safest place for them.
              </p>
              <StyledCodesPanel>
                {backupCodes.map((code) => (
                  <code key={code}>{code}</code>
                ))}
              </StyledCodesPanel>
              <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <StyledGhostButton
                  type="button"
                  onClick={() => copy(backupCodes.join('\n'), 'codes')}
                >
                  {copied === 'codes' ? 'Copied' : 'Copy all'}
                </StyledGhostButton>
                <StyledGhostButton type="button" onClick={downloadCodes}>
                  Download .txt
                </StyledGhostButton>
              </div>
              <StyledConfirmRow>
                <input
                  type="checkbox"
                  checked={saved}
                  onChange={(e) => setSaved(e.target.checked)}
                />
                <span className="box">{saved ? '✓' : ''}</span>
                I&apos;ve saved these codes somewhere safe
              </StyledConfirmRow>
            </StyledStepBody>
          )}

          {step === 4 && (
            <StyledDone>
              <div className="tick">✓</div>
              <h2>Two-factor is on</h2>
              <p>
                From your next sign-in, Nxsys will ask for a code from your
                authenticator app after your password.
              </p>
              <div className="actions">
                {/*
                  The handoff also shows "Add another method". Left out on
                  purpose: there is exactly one method today, so the button
                  would do nothing. Worth restoring the moment a second one
                  exists — the step shape already allows for it.
                */}
                <StyledPrimaryButton
                  type="button"
                  onClick={() => session && onComplete(session)}
                >
                  Done
                </StyledPrimaryButton>
              </div>
            </StyledDone>
          )}

          {step < 4 && (
            <StyledFooter>
              {/* Step 3 is the one step with no left button (Vincent, review of
                  #1755): by then `confirmSetup` has already succeeded, so 2FA is
                  on and the challenge token is spent. Going back landed on the
                  code step, which can never verify again and has no forward
                  button — with the recovery codes shown once and now
                  unreachable. `justify-content: space-between` on the footer
                  would pull the primary button left with no sibling, hence the
                  spacer. */}
              {step === 3 ? (
                <span />
              ) : (
                <StyledTextButton type="button" onClick={goBack}>
                  {backLabel}
                </StyledTextButton>
              )}
              {/* The code step submits itself on the sixth digit, so there is no
                  button to press (Thang, 17/08). Progress is reported by the
                  hint under the row reading "Checking…". The spacer keeps Back
                  on the left, same as step 0 does. */}
              {step === 2 ? (
                <span />
              ) : (
                <StyledPrimaryButton
                  type="button"
                  disabled={primaryDisabled}
                  onClick={goNext}
                >
                  {primaryLabel}
                </StyledPrimaryButton>
              )}
            </StyledFooter>
          )}
        </StyledPanel>
      </StyledDialog>
    </StyledOverlay>
  );
};

export default TwoFactorSetup;
